import usePrefilledVarietyFormats from '../../hooks/usePrefilledVarietyFormats';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiImage,
  FiPlus,
  FiTrash2,
  FiUpload,
  FiX,
} from 'react-icons/fi';

import useProductFormatOptions from '../../hooks/useProductFormatOptions';
import { isRepeatableProductFormat } from '../../utils/varietyFormatIdentity';
import InfoTooltip from '../InfoTooltip';
import {
  buildFormatDraft,
  buildInitialProductDraft,
  buildVarietyDraft,
  hasValidationErrors,
  resolveSelectedCategory,
  validateFormatDrafts,
  validateProductDraft,
  validateVarietyDrafts,
} from './productCreationDrafts';

const STEPS = [
  { id: 'product', label: 'Producto', description: 'Identidad y operación' },
  { id: 'varieties', label: 'Variedades', description: 'Tipos de inventario' },
  { id: 'formats', label: 'Formatos', description: 'Cómo se vende cada variedad' },
];

const FIELD_BASE_CLASS = 'mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-950 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500';
const FIELD_ERROR_CLASS = 'border-red-400 focus:border-red-500 focus:ring-red-100';

const getFocusableElements = (container) => Array.from(container?.querySelectorAll(
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
) || []).filter((element) => element.getAttribute('aria-hidden') !== 'true');

const FieldLabel = ({ htmlFor, children, help, required = false }) => (
  <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
    <label htmlFor={htmlFor}>{children}{required ? <span className="text-red-500"> *</span> : null}</label>
    {help ? <InfoTooltip message={help} label="Más información sobre este campo" /> : null}
  </div>
);

const FieldError = ({ children, id }) => (
  children ? <p id={id} role="alert" className="mt-1 text-xs font-medium text-red-600">{children}</p> : null
);

const getErrorProps = (id, error) => ({
  'aria-invalid': error ? 'true' : undefined,
  'aria-describedby': error ? `${id}-error` : undefined,
});

const SectionIntro = ({ eyebrow, title, description, aside, headingId }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
    <div>
      {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.16em] text-blue-700">{eyebrow}</p> : null}
      <h2 id={headingId} tabIndex={-1} className="mt-1 text-2xl font-semibold tracking-tight text-gray-950 outline-none">{title}</h2>
      <p className="mt-1 max-w-3xl text-sm leading-relaxed text-gray-600">{description}</p>
    </div>
    {aside}
  </div>
);

const ImagePicker = ({ id, file, onChange, disabled, compact = false, label = 'Imagen' }) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!file || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
      setPreviewUrl('');
      return undefined;
    }
    const nextUrl = URL.createObjectURL(file);
    setPreviewUrl(nextUrl);
    return () => URL.revokeObjectURL?.(nextUrl);
  }, [file]);

  return (
    <div className={`rounded-2xl border border-gray-200 bg-gray-50 ${compact ? 'p-3' : 'p-4'}`}>
      <div className="flex items-center gap-3">
        <div className={`${compact ? 'h-16 w-16' : 'h-24 w-24 sm:h-32 sm:w-32'} relative shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white`}>
          {previewUrl ? (
            <img src={previewUrl} alt={`Vista previa de ${label.toLocaleLowerCase('es')}`} className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-gray-400">
              <FiImage className={compact ? 'text-xl' : 'text-3xl'} aria-hidden="true" />
              {!compact && <span className="text-[11px] font-medium">Sin imagen</span>}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">{label}</p>
          <p className="mt-0.5 text-xs text-gray-500">JPG, PNG o WebP. Opcional.</p>
          {file ? <p className="mt-1 truncate text-xs font-medium text-gray-700">{file.name}</p> : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={disabled}
              aria-controls={id}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiUpload aria-hidden="true" />
              Elegir
            </button>
            <input
              ref={inputRef}
              id={id}
              type="file"
              accept="image/*"
              disabled={disabled}
              tabIndex={-1}
              aria-hidden="true"
              className="sr-only"
              onChange={(event) => {
                onChange(event.target.files?.[0] || null);
                event.target.value = '';
              }}
            />
            {file ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onChange(null)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Quitar
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stepper = ({ activeStep, highestStep, onSelect, disabled = false }) => (
  <nav aria-label="Progreso de creación" className="border-b border-gray-200 bg-white px-4 py-3 sm:px-6 lg:px-8">
    <ol className="mx-auto grid max-w-7xl grid-cols-3 gap-2 sm:gap-4">
      {STEPS.map((step, index) => {
        const isActive = index === activeStep;
        const isComplete = index < activeStep || index < highestStep;
        const canSelect = index <= highestStep;
        return (
          <li key={step.id} className="min-w-0">
            <button
              type="button"
              disabled={disabled || !canSelect}
              onClick={() => onSelect(index)}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Paso ${index + 1} de ${STEPS.length}: ${step.label}${isComplete ? ', completado' : ''}`}
              className={`group flex w-full items-center gap-2 rounded-xl px-1 py-1 text-left sm:px-2 ${canSelect && !disabled ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-bold transition ${
                isActive
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm ring-4 ring-blue-100'
                  : isComplete
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-gray-300 bg-white text-gray-500'
              }`}>
                {isComplete && !isActive ? <FiCheck aria-hidden="true" /> : index + 1}
              </span>
              <span className="hidden min-w-0 sm:block">
                <span className={`block truncate text-xs font-semibold ${isActive ? 'text-blue-700' : 'text-gray-800'}`}>{step.label}</span>
                <span className="hidden truncate text-[11px] text-gray-500 lg:block">{step.description}</span>
              </span>
            </button>
          </li>
        );
      })}
    </ol>
    <p className="mt-2 text-center text-xs font-medium text-gray-600 sm:hidden">
      Paso {activeStep + 1} de {STEPS.length}: {STEPS[activeStep].label}
    </p>
  </nav>
);

const ProductStep = ({
  productDraft,
  onPatch,
  categories,
  categoriesLoading,
  categoriesError,
  taxes,
  taxesLoading,
  taxesError,
  canReadTaxes,
  errors,
  disabled,
}) => {
  const selectedCategory = resolveSelectedCategory(productDraft, categories);
  const subcategories = (selectedCategory?.CategorySubcategories || []).slice().sort((a, b) => (
    Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)
    || String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
  ));
  const patch = onPatch;

  return (
    <div className="space-y-6">
      <SectionIntro
        headingId="wizard-step-product-heading"
        eyebrow="Paso 1"
        title="Define el producto"
        description="Comienza con la identidad compartida del catálogo. Luego agrega sus variedades y los detalles de cada formato de venta."
      />

      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <ImagePicker
          id="wizard-product-image"
          file={productDraft.imageFile}
          onChange={(imageFile) => patch({ imageFile })}
          disabled={disabled}
          label="Imagen del producto"
        />

        <div className="space-y-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div>
            <FieldLabel htmlFor="wizard-product-name" required help="Nombre principal que verán los usuarios al buscar el producto.">Nombre</FieldLabel>
            <input
              id="wizard-product-name"
              autoFocus
              autoComplete="off"
              value={productDraft.productName}
              disabled={disabled}
              aria-required="true"
              {...getErrorProps('wizard-product-name', errors?.productName)}
              onChange={(event) => patch({ productName: event.target.value })}
              className={`${FIELD_BASE_CLASS} ${errors?.productName ? FIELD_ERROR_CLASS : ''}`}
              placeholder="Ej. Manzana"
            />
            <FieldError id="wizard-product-name-error">{errors?.productName}</FieldError>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="wizard-product-category" required help="La categoría organiza el producto en el catálogo.">Categoría</FieldLabel>
              <select
                id="wizard-product-category"
                value={productDraft.categoryId}
                disabled={disabled || categoriesLoading}
                aria-required="true"
                {...getErrorProps('wizard-product-category', errors?.categoryId)}
                onChange={(event) => patch({ categoryId: event.target.value, subcategoryId: '' })}
                className={`${FIELD_BASE_CLASS} ${errors?.categoryId ? FIELD_ERROR_CLASS : ''}`}
              >
                <option value="">Selecciona una categoría</option>
                {categories.filter((category) => category?.isActive !== false).map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
              {categoriesLoading ? <p role="status" className="mt-1 text-xs text-gray-500">Cargando categorías...</p> : null}
              {!categoriesLoading && categoriesError ? <FieldError>{categoriesError}</FieldError> : null}
              <FieldError id="wizard-product-category-error">{errors?.categoryId}</FieldError>
            </div>
            <div>
              <FieldLabel htmlFor="wizard-product-subcategory" help="Agrupación opcional dentro de la categoría para facilitar búsquedas y reportes.">Subcategoría</FieldLabel>
              <select
                id="wizard-product-subcategory"
                value={productDraft.subcategoryId}
                disabled={disabled || !selectedCategory}
                {...getErrorProps('wizard-product-subcategory', errors?.subcategoryId)}
                onChange={(event) => patch({ subcategoryId: event.target.value })}
                className={`${FIELD_BASE_CLASS} ${errors?.subcategoryId ? FIELD_ERROR_CLASS : ''}`}
              >
                <option value="">Sin subcategoría</option>
                {subcategories.map((subcategory) => (
                  <option key={subcategory.id} value={subcategory.id}>{subcategory.name}</option>
                ))}
              </select>
              <FieldError id="wizard-product-subcategory-error">{errors?.subcategoryId}</FieldError>
            </div>
          </div>



          <div>
            <p className="text-sm text-gray-600">Los códigos de barras nuevos deben ser únicos en toda la empresa.</p>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start gap-2">
              <label className="flex min-w-0 flex-1 items-start gap-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={productDraft.useDefaultTaxes}
                  disabled={disabled || !canReadTaxes || taxesLoading || Boolean(taxesError)}
                  onChange={(event) => patch({ useDefaultTaxes: event.target.checked })}
                  className="mt-0.5 accent-blue-600"
                />
                <span className="min-w-0">
                  <span className="block font-semibold text-gray-950">Usar impuestos por defecto</span>
                  <span className="mt-0.5 block text-xs text-gray-500">Recomendado para la mayoría de los productos.</span>
                </span>
              </label>
              <InfoTooltip message="Aplica automáticamente la configuración tributaria predeterminada del negocio. Desactívalo para elegir impuestos específicos." />
            </div>
            {!canReadTaxes ? <p className="mt-3 text-xs text-gray-500">No tienes permiso para consultar impuestos; se usarán los predeterminados.</p> : null}
            {canReadTaxes && taxesLoading ? <p role="status" className="mt-3 text-xs text-gray-500">Cargando impuestos...</p> : null}
            {canReadTaxes && !taxesLoading && taxesError ? <FieldError>{taxesError}</FieldError> : null}
            {canReadTaxes && !taxesLoading && !taxesError && !productDraft.useDefaultTaxes ? (
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {!taxes.length ? <p className="text-xs text-gray-500 sm:col-span-2">No hay impuestos activos en esta sucursal.</p> : null}
                {taxes.map((tax) => {
                  const checked = productDraft.taxIds.some((id) => String(id) === String(tax.id));
                  return (
                    <label key={tax.id} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={(event) => patch({
                          taxIds: event.target.checked
                            ? [...productDraft.taxIds, tax.id]
                            : productDraft.taxIds.filter((id) => String(id) !== String(tax.id)),
                        })}
                        className="accent-blue-600"
                      />
                      {tax.name} ({Number(tax.rate || 0)}%)
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const VarietiesStep = ({
  varieties,
  onPatch,
  onAdd,
  onRemove,
  errors,
  disabled,
  creationProgress,
}) => (
  <div className="space-y-6">
    <SectionIntro
      headingId="wizard-step-varieties-heading"
      eyebrow="Paso 2"
      title="Agrega las variedades"
      description="Cada variedad representa una identidad de inventario del producto. Luego podrás definir uno o más formatos de venta para cada una."
      aside={(
        <button
          id="wizard-add-variety"
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2.5 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-50 disabled:opacity-50"
        >
          <FiPlus aria-hidden="true" /> Agregar variedad
        </button>
      )}
    />
    {errors?._form ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{errors._form}</p> : null}
    <div className="space-y-4">
      {varieties.map((variety, index) => {
        const rowErrors = errors?.[variety._draftKey] || {};
        const isSaved = Boolean(creationProgress?.completedVarieties?.[variety._draftKey]?.variety);
        const rowDisabled = disabled || isSaved;
        return (
          <article key={variety._draftKey} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-gray-100 pb-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Variedad {index + 1}</p>
                <h3 className="text-base font-semibold text-gray-950">{variety.varietyName.trim() || 'Nueva variedad'}</h3>
                {isSaved ? <span className="mt-1 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Guardada</span> : null}
              </div>
              {varieties.length > 1 && !isSaved ? (
                <button
                  type="button"
                  onClick={() => onRemove(variety._draftKey)}
                  disabled={rowDisabled}
                  aria-label={`Eliminar variedad ${index + 1}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  <FiTrash2 aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
              <ImagePicker
                id={`wizard-variety-image-${variety._draftKey}`}
                file={variety.imageFile}
                onChange={(imageFile) => onPatch(variety._draftKey, { imageFile })}
                disabled={rowDisabled}
                compact
                label="Imagen de variedad"
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel htmlFor={`wizard-variety-name-${variety._draftKey}`} required help="Nombre que distingue esta variedad dentro del producto, por ejemplo Gala, Fuji o Premium.">Nombre</FieldLabel>
                  <input
                    id={`wizard-variety-name-${variety._draftKey}`}
                    value={variety.varietyName}
                    disabled={rowDisabled}
                    aria-required="true"
                    {...getErrorProps(`wizard-variety-name-${variety._draftKey}`, rowErrors.varietyName)}
                    onChange={(event) => onPatch(variety._draftKey, { varietyName: event.target.value })}
                    className={`${FIELD_BASE_CLASS} ${rowErrors.varietyName ? FIELD_ERROR_CLASS : ''}`}
                    placeholder="Ej. Gala"
                  />
                  <FieldError id={`wizard-variety-name-${variety._draftKey}-error`}>{rowErrors.varietyName}</FieldError>
                </div>
                <div>
                  <FieldLabel htmlFor={`wizard-variety-base-${variety._draftKey}`} required help="Unidad en la que se controla el inventario. Los formatos de venta se convertirán a esta unidad mediante su equivalencia.">Unidad base de inventario</FieldLabel>
                  <select
                    id={`wizard-variety-base-${variety._draftKey}`}
                    value={variety.inventoryBaseUnit}
                    disabled={rowDisabled}
                    aria-required="true"
                    {...getErrorProps(`wizard-variety-base-${variety._draftKey}`, rowErrors.inventoryBaseUnit)}
                    onChange={(event) => onPatch(variety._draftKey, { inventoryBaseUnit: event.target.value })}
                    className={`${FIELD_BASE_CLASS} ${rowErrors.inventoryBaseUnit ? FIELD_ERROR_CLASS : ''}`}
                  >
                    <option value="kg">Kilogramo</option>
                    <option value="unidad">Unidad</option>
                  </select>
                  <FieldError id={`wizard-variety-base-${variety._draftKey}-error`}>{rowErrors.inventoryBaseUnit}</FieldError>
                </div>
                <div className="sm:col-span-2">
                  <FieldLabel htmlFor={`wizard-variety-description-${variety._draftKey}`} help="Detalle opcional para reconocer la variedad internamente.">Descripción</FieldLabel>
                  <textarea
                    id={`wizard-variety-description-${variety._draftKey}`}
                    value={variety.description}
                    disabled={rowDisabled}
                    onChange={(event) => onPatch(variety._draftKey, { description: event.target.value })}
                    className={FIELD_BASE_CLASS}
                    rows={2}
                    placeholder="Descripción opcional"
                  />
                </div>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  </div>
);

const FormatIdentityCard = ({
  variety,
  format,
  index,
  formatOptions,
  errors,
  disabled,
  isSaved,
  canRemove,
  onPatch,
  onRemove,
}) => (
  <article className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
    <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Formato {index + 1}</p>
        <h4 className="font-semibold text-gray-950">
          {formatOptions.find((option) => option.value === format.productFormatCode)?.label || 'Nuevo formato'}
          {' · '}{format.metricUnit || 'sin unidad'}
        </h4>
        {isSaved ? <span className="mt-1 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Guardado</span> : null}
      </div>
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled || format.isDefault}
          aria-label={`Eliminar formato ${index + 1} de ${variety.varietyName}`}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <FiTrash2 aria-hidden="true" />
        </button>
      ) : null}
    </div>

    <div className="grid gap-4 xl:grid-cols-[180px_minmax(0,1fr)]">
      <ImagePicker
        id={`wizard-format-image-${format._draftKey}`}
        file={format.imageFile}
        onChange={(imageFile) => onPatch({ imageFile })}
        disabled={disabled}
        compact
        label="Imagen del formato"
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <FieldLabel htmlFor={`wizard-format-code-${format._draftKey}`} required help="Presentación comercial compartida, como Detalle o Mayorista.">Formato de producto</FieldLabel>
          <select
            id={`wizard-format-code-${format._draftKey}`}
            value={format.productFormatCode}
            disabled={disabled || format.isDefault}
            aria-required="true"
            {...getErrorProps(`wizard-format-code-${format._draftKey}`, errors?.productFormatCode)}
            onChange={(event) => onPatch({ productFormatCode: event.target.value })}
            className={`${FIELD_BASE_CLASS} ${errors?.productFormatCode ? FIELD_ERROR_CLASS : ''}`}
          >
            {formatOptions.map((option) => <option key={option.value} value={option.value} disabled={!isRepeatableProductFormat(option) && option.value !== format.productFormatCode && variety.formats.some((other) => other._draftKey !== format._draftKey && other.productFormatCode === option.value)}>{option.label}</option>)}
          </select>
          <FieldError id={`wizard-format-code-${format._draftKey}-error`}>{errors?.productFormatCode}</FieldError>
        </div>
        <div>
          <FieldLabel htmlFor={`wizard-format-unit-${format._draftKey}`} help="Unidad que verá el cliente al comprar este formato, por ejemplo unidad, caja, bandeja o kg. Puedes completarla después en Lista de precios.">Unidad de venta</FieldLabel>
          <input
            id={`wizard-format-unit-${format._draftKey}`}
            value={format.metricUnit}
            disabled={disabled}
            {...getErrorProps(`wizard-format-unit-${format._draftKey}`, errors?.metricUnit)}
            onChange={(event) => onPatch({ metricUnit: event.target.value })}
            className={`${FIELD_BASE_CLASS} ${errors?.metricUnit ? FIELD_ERROR_CLASS : ''}`}
            placeholder="Ej. caja"
          />
          <FieldError id={`wizard-format-unit-${format._draftKey}-error`}>{errors?.metricUnit}</FieldError>
        </div>
        <div>
          <FieldLabel htmlFor={`wizard-format-barcode-${format._draftKey}`} help="Código de hasta 7 dígitos, único en toda la empresa. Puedes editar el código propuesto.">Código de barras</FieldLabel>
          <input
            id={`wizard-format-barcode-${format._draftKey}`}
            inputMode="numeric"
            maxLength={7}
            value={format.barcode}
            disabled={disabled}
            {...getErrorProps(`wizard-format-barcode-${format._draftKey}`, errors?.barcode)}
            onChange={(event) => onPatch({ barcode: event.target.value.replace(/\D/g, '').slice(0, 7) })}
            className={`${FIELD_BASE_CLASS} ${errors?.barcode ? FIELD_ERROR_CLASS : ''}`}
            placeholder="Opcional"
          />
          <FieldError id={`wizard-format-barcode-${format._draftKey}-error`}>{errors?.barcode}</FieldError>
        </div>
        <div>
          <FieldLabel htmlFor={`wizard-format-weight-${format._draftKey}`} help="Peso real de una unidad de venta. También puede usarse como equivalencia de inventario.">Peso por unidad (kg)</FieldLabel>
          <input
            id={`wizard-format-weight-${format._draftKey}`}
            type="number"
            min="0"
            step="0.001"
            value={format.unitWeightKg}
            disabled={disabled || format.useScaleWeight}
            {...getErrorProps(`wizard-format-weight-${format._draftKey}`, errors?.unitWeightKg)}
            onChange={(event) => onPatch({ unitWeightKg: event.target.value })}
            className={`${FIELD_BASE_CLASS} ${errors?.unitWeightKg ? FIELD_ERROR_CLASS : ''}`}
            placeholder={format.useScaleWeight ? 'Balanza' : 'Ej. 5'}
          />
          <FieldError id={`wizard-format-weight-${format._draftKey}-error`}>{errors?.unitWeightKg}</FieldError>
        </div>
        <div>
          <FieldLabel htmlFor={`wizard-format-description-${format._draftKey}`} help="Texto corto para diferenciar la presentación, por ejemplo Caja 10 kg.">Descripción del precio</FieldLabel>
          <input
            id={`wizard-format-description-${format._draftKey}`}
            value={format.description}
            disabled={disabled}
            onChange={(event) => onPatch({ description: event.target.value })}
            className={FIELD_BASE_CLASS}
            placeholder="Opcional"
          />
        </div>
        <div className="flex items-start gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 sm:col-span-2 lg:col-span-3">
          <label className="flex min-w-0 flex-1 items-start gap-3">
            <input
              type="checkbox"
              checked={format.useScaleWeight}
              disabled={disabled}
              onChange={(event) => onPatch({
                useScaleWeight: event.target.checked,
                unitWeightKg: event.target.checked ? '' : format.unitWeightKg,
              })}
              className="mt-0.5 accent-blue-600"
            />
            <span>
              <span className="block font-semibold text-gray-900">Usar peso de balanza</span>
              <span className="mt-0.5 block text-xs text-gray-500">Ideal para venta a granel o peso variable.</span>
            </span>
          </label>
          <InfoTooltip message="Actívalo cuando la cantidad vendida se obtiene directamente desde una balanza." />
        </div>
      </div>
    </div>
  </article>
);

const FormatsStep = ({
  varieties,
  formatOptions,
  errors,
  disabled,
  onAddFormat,
  onRemoveFormat,
  onPatchFormat,
  creationProgress,
  permissionError,
  submitError,
  formatDefaults,
  hasPartialProgress,
}) => (
  <div className="space-y-6">
    <SectionIntro
      headingId="wizard-step-formats-heading"
      eyebrow="Paso 3"
      title="Configura los formatos de venta"
      description="Define la imagen, el formato, la unidad de venta, el código, el peso y el uso de balanza para cada variedad."
    />
    {permissionError ? <p role="alert" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{permissionError}</p> : null}
    {hasPartialProgress ? (
      <p role="status" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        Parte del producto ya se guardó. Puedes reintentar sin duplicar lo completado; evita cerrar este flujo hasta finalizar.
      </p>
    ) : null}
    {formatDefaults?.loading && <p role="status" className="text-sm text-gray-500">Proponiendo formatos y códigos...</p>}
    {formatDefaults?.error && <button type="button" onClick={formatDefaults.retry} className="text-sm text-blue-700 underline">Reintentar formatos</button>}
    {!formatDefaults?.loading && varieties.every((variety) => variety.formatsInitialized && !variety.formats.length) && <p className="text-sm text-gray-600">La categoría no tiene formatos para prellenar. Puedes crear las variedades así o añadir presentaciones manualmente.</p>}
    {submitError ? <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{submitError}</p> : null}
    <div className="space-y-6">
      {varieties.map((variety) => {
        const varietyErrors = errors?.[variety._draftKey] || {};
        const varietyProgress = creationProgress?.completedVarieties?.[variety._draftKey];
        return (
          <section key={variety._draftKey} className="rounded-3xl border border-blue-100 bg-blue-50/50 p-3 sm:p-5">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Variedad</p>
                <h3 className="text-lg font-semibold text-gray-950">{variety.varietyName}</h3>
                <p className="text-xs text-gray-500">Inventario base: {variety.inventoryBaseUnit}</p>
              </div>
              <button
                id={`wizard-add-format-${variety._draftKey}`}
                type="button"
                onClick={() => onAddFormat(variety._draftKey)}
                disabled={disabled || !formatOptions.some((option) => isRepeatableProductFormat(option) || !(variety.formats || []).some((format) => format.productFormatCode === option.value))}
                title="Pack permite varias presentaciones con distinta unidad o peso. Los demás formatos permiten una."
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 shadow-sm hover:bg-blue-50 disabled:opacity-50"
              >
                <FiPlus aria-hidden="true" /> Agregar formato
              </button>
            </div>
            {varietyErrors?._form ? <p role="alert" className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{varietyErrors._form}</p> : null}
            <div className="space-y-3">
              {(variety.formats || []).map((format, index) => (
                (() => {
                  const isSaved = Object.prototype.hasOwnProperty.call(
                    varietyProgress?.completedFormats || {},
                    format._draftKey
                  );
                  return (
                    <FormatIdentityCard
                      key={format._draftKey}
                      variety={variety}
                      format={format}
                      index={index}
                      formatOptions={formatOptions}
                      errors={varietyErrors?.[format._draftKey]}
                      disabled={disabled || isSaved}
                      isSaved={isSaved}
                      canRemove={variety.formats.length > 1 && !isSaved}
                      onPatch={(patch) => onPatchFormat(variety._draftKey, format._draftKey, patch)}
                      onRemove={() => onRemoveFormat(variety._draftKey, format._draftKey)}
                    />
                  );
                })()
              ))}
            </div>
          </section>
        );
      })}
    </div>
  </div>
);

const ProductCreationWizard = ({
  branchId,
  isOpen = true,
  categories = [],
  categoriesLoading = false,
  categoriesError = '',
  taxes = [],
  taxesLoading = false,
  taxesError = '',
  canReadTaxes = true,
  canCreateProduct = true,
  canUpdateProduct = true,
  isSaving = false,
  hasPartialProgress = false,
  creationProgress = null,
  saveStatus = '',
  onClose,
  onSubmit,
}) => {
  const formatOptions = useProductFormatOptions();
  const [activeStep, setActiveStep] = useState(0);
  const [highestStep, setHighestStep] = useState(0);
  const [productDraft, setProductDraft] = useState(buildInitialProductDraft);
  const [varieties, setVarieties] = useState(() => [buildVarietyDraft('variety-1')]);
  const formatDefaults = usePrefilledVarietyFormats({ branchId, categoryId: productDraft.categoryId, varieties, setVarieties, enabled: isOpen });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [draftAnnouncement, setDraftAnnouncement] = useState('');
  const varietySequence = useRef(1);
  const formatSequence = useRef(0);
  const dialogRef = useRef(null);
  const scrollRef = useRef(null);
  const previousFocusRef = useRef(null);
  const selectedCategory = resolveSelectedCategory(productDraft, categories);
  const disabled = isSaving;
  const productWasSaved = Boolean(creationProgress?.product);
  const canCompleteFlow = canCreateProduct && canUpdateProduct;
  const permissionError = !canCreateProduct
    ? 'Necesitas permiso para crear productos.'
    : !canUpdateProduct
      ? 'Necesitas permiso para crear variedades y formatos.'
      : '';

  const requestClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty && !hasPartialProgress && typeof window !== 'undefined') {
      const shouldClose = window.confirm(
        'Tienes cambios sin guardar. ¿Cerrar y descartar este borrador?'
      );
      if (!shouldClose) return;
    }
    onClose?.();
  }, [hasPartialProgress, isDirty, isSaving, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setActiveStep(0);
    setHighestStep(0);
    setProductDraft(buildInitialProductDraft());
    setVarieties([buildVarietyDraft('variety-1')]);
    setErrors({});
    setSubmitError('');
    setIsDirty(false);
    setDraftAnnouncement('');
    varietySequence.current = 1;
    formatSequence.current = 0;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeStep !== 0) return;
    const defaultBaseUnit = selectedCategory?.isResaleOnly ? 'unidad' : 'kg';
    setVarieties((current) => current.map((variety) => (
      !variety.varietyName.trim() && !variety.description.trim()
        ? { ...variety, inventoryBaseUnit: defaultBaseUnit }
        : variety
    )));
  }, [activeStep, isOpen, selectedCategory?.isResaleOnly]);

  useEffect(() => {
    if (!isOpen || !taxesError) return;
    setProductDraft((current) => (
      current.useDefaultTaxes
        ? current
        : { ...current, useDefaultTaxes: true, taxIds: [] }
    ));
  }, [isOpen, taxesError]);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const root = document.getElementById('root');
    const previousOverflow = document.body.style.overflow;
    const previousAriaHidden = root?.getAttribute('aria-hidden');
    const previousInert = root?.inert;
    previousFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    const preferredInput = dialogRef.current?.querySelector('#wizard-product-name');
    const fallback = dialogRef.current?.querySelector('input:not([disabled]), button:not([disabled])');
    (preferredInput || fallback)?.focus();
    if (root) {
      root.setAttribute('aria-hidden', 'true');
      root.inert = true;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      if (root) {
        root.inert = previousInert;
        if (previousAriaHidden === null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousAriaHidden);
      }
      previousFocusRef.current?.focus?.();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isSaving) requestClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, requestClose]);

  const markDraftChanged = () => {
    if (submitError) setSubmitError('');
    setIsDirty(true);
    setHighestStep((current) => Math.min(current, activeStep));
  };

  const patchProduct = (patch) => {
    markDraftChanged();
    setProductDraft((current) => ({ ...current, ...patch }));
    setErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
  };

  const patchVariety = (varietyKey, patch) => {
    markDraftChanged();
    setVarieties((current) => current.map((variety) => (
      variety._draftKey === varietyKey ? { ...variety, ...patch } : variety
    )));
    setErrors((current) => {
      const row = current?.[varietyKey];
      if (!row) return current;
      const nextRow = { ...row };
      Object.keys(patch).forEach((key) => delete nextRow[key]);
      const next = { ...current };
      if (Object.keys(nextRow).length) next[varietyKey] = nextRow;
      else delete next[varietyKey];
      return next;
    });
  };

  const patchFormat = (varietyKey, formatKey, patch) => {
    markDraftChanged();
    setVarieties((current) => current.map((variety) => (
      variety._draftKey === varietyKey
        ? {
          ...variety,
          formats: variety.formats.map((format) => (
            format._draftKey === formatKey ? { ...format, ...patch, defaultEdited: true, ...(Object.hasOwn(patch, 'barcode') ? { barcodeIsSuggested: false } : {}) } : format
          )),
        }
        : variety
    )));
    setErrors((current) => {
      const varietyErrors = current?.[varietyKey];
      const row = varietyErrors?.[formatKey];
      if (!row) return current;
      const nextRow = { ...row };
      Object.keys(patch).forEach((key) => delete nextRow[key]);
      const nextVarietyErrors = { ...varietyErrors };
      if (Object.keys(nextRow).length) nextVarietyErrors[formatKey] = nextRow;
      else delete nextVarietyErrors[formatKey];
      const next = { ...current };
      if (Object.keys(nextVarietyErrors).length) next[varietyKey] = nextVarietyErrors;
      else delete next[varietyKey];
      return next;
    });
  };

  const addVariety = () => {
    varietySequence.current += 1;
    const varietyKey = `variety-${varietySequence.current}`;
    setVarieties((current) => [
      ...current,
      buildVarietyDraft(varietyKey, {
        isResaleOnly: Boolean(selectedCategory?.isResaleOnly),
      }),
    ]);
    markDraftChanged();
    setErrors({});
    setDraftAnnouncement('Variedad agregada.');
    window.setTimeout(() => {
      dialogRef.current?.querySelector(`#wizard-variety-name-${varietyKey}`)?.focus();
    }, 0);
  };

  const removeVariety = (varietyKey) => {
    setVarieties((current) => current.filter((variety) => variety._draftKey !== varietyKey));
    markDraftChanged();
    setErrors({});
    setDraftAnnouncement('Variedad eliminada.');
    window.setTimeout(() => {
      const nextField = dialogRef.current?.querySelector('[id^="wizard-variety-name-"]:not([disabled])');
      (nextField || dialogRef.current?.querySelector('#wizard-add-variety'))?.focus();
    }, 0);
  };

  const nextFormatDraft = () => {
    formatSequence.current += 1;
    return buildFormatDraft(
      `format-${formatSequence.current}`,
      formatOptions[0]?.value || 'detalle'
    );
  };

  const ensureFormats = (sourceVarieties) => sourceVarieties.map((variety) => (
    variety.formats?.length ? variety : { ...variety, formats: [nextFormatDraft()] }
  ));

  const addFormat = (varietyKey) => {
    const variety = varieties.find((entry) => entry._draftKey === varietyKey);
    const unusedOption = formatOptions.find((option) => isRepeatableProductFormat(option) || !(variety?.formats || []).some((format) => format.productFormatCode === option.value));
    if (!unusedOption) return;
    const formatDraft = nextFormatDraft();
    formatDraft.productFormatCode = unusedOption.value;
    setVarieties((current) => current.map((variety) => (
      variety._draftKey === varietyKey
        ? { ...variety, formats: [...(variety.formats || []), formatDraft] }
        : variety
    )));
    markDraftChanged();
    setErrors({});
    setDraftAnnouncement('Formato agregado.');
    window.setTimeout(() => {
      dialogRef.current?.querySelector(`#wizard-format-unit-${formatDraft._draftKey}`)?.focus();
    }, 0);
  };

  const removeFormat = (varietyKey, formatKey) => {
    setVarieties((current) => current.map((variety) => (
      variety._draftKey === varietyKey
        ? { ...variety, formats: variety.formats.filter((format) => format._draftKey !== formatKey || format.isDefault) }
        : variety
    )));
    markDraftChanged();
    setErrors({});
    setDraftAnnouncement('Formato eliminado.');
    window.setTimeout(() => {
      const nextField = dialogRef.current?.querySelector('[id^="wizard-format-unit-"]:not([disabled])');
      (nextField || dialogRef.current?.querySelector(`#wizard-add-format-${varietyKey}`))?.focus();
    }, 0);
  };

  const validateStep = (step) => {
    let nextErrors = {};
    if (step === 0) nextErrors = validateProductDraft(productDraft, categories);
    if (step === 1) nextErrors = validateVarietyDrafts(varieties);
    if (step === 2) {
      nextErrors = validateFormatDrafts({
        varieties,
        formatOptions,
        barcodeScope: productDraft.barcodeScope,
      });
    }
    setErrors(nextErrors);
    return !hasValidationErrors(nextErrors);
  };

  const focusStepContent = (step, { firstInvalid = false } = {}) => {
    window.setTimeout(() => {
      if (scrollRef.current && typeof scrollRef.current.scrollTo === 'function') {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
      const invalidField = firstInvalid
        ? dialogRef.current?.querySelector('[aria-invalid="true"]:not([disabled])')
        : null;
      const stepHeading = dialogRef.current?.querySelector(`#wizard-step-${STEPS[step].id}-heading`);
      (invalidField || stepHeading)?.focus();
    }, 0);
  };

  const moveToStep = (step) => {
    setActiveStep(step);
    setHighestStep((current) => Math.max(current, step));
    setErrors({});
    setSubmitError('');
    focusStepContent(step);
  };

  const goNext = () => {
    if (!validateStep(activeStep)) {
      focusStepContent(activeStep, { firstInvalid: true });
      return;
    }
    if (activeStep === 1 && !branchId) {
      setVarieties((current) => ensureFormats(current));
    }
    moveToStep(Math.min(activeStep + 1, STEPS.length - 1));
  };

  const handleSubmit = async () => {
    if (!canCompleteFlow) return;
    if (branchId && (formatDefaults.loading || formatDefaults.error)) {
      setSubmitError(formatDefaults.error || 'Espera a que se propongan los formatos.');
      return;
    }
    const allStepErrors = [
      validateProductDraft(productDraft, categories),
      validateVarietyDrafts(varieties),
      validateFormatDrafts({
        varieties,
        formatOptions,
        barcodeScope: productDraft.barcodeScope,
      }),
    ];
    const firstInvalidStep = allStepErrors.findIndex(hasValidationErrors);
    if (firstInvalidStep >= 0) {
      setErrors(allStepErrors[firstInvalidStep]);
      setActiveStep(firstInvalidStep);
      focusStepContent(firstInvalidStep, { firstInvalid: true });
      return;
    }
    setErrors({});
    setSubmitError('');
    try {
      await onSubmit?.({ productDraft, varietyDrafts: varieties });
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'No se pudo crear el producto completo.'
      );
    }
  };

  const handleDialogKeyDown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusableElements(dialogRef.current);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const stepContent = useMemo(() => {
    if (activeStep === 0) {
      return (
        <ProductStep
          productDraft={productDraft}
          onPatch={patchProduct}
          categories={categories}
          categoriesLoading={categoriesLoading}
          categoriesError={categoriesError}
          taxes={taxes}
          taxesLoading={taxesLoading}
          taxesError={taxesError}
          canReadTaxes={canReadTaxes}
          errors={errors}
          disabled={disabled || productWasSaved}
        />
      );
    }
    if (activeStep === 1) {
      return (
        <VarietiesStep
          varieties={varieties}
          onPatch={patchVariety}
          onAdd={addVariety}
          onRemove={removeVariety}
          errors={errors}
          disabled={disabled}
          creationProgress={creationProgress}
        />
      );
    }
    return (
      <FormatsStep
        varieties={varieties}
        formatOptions={formatOptions}
        errors={errors}
        disabled={disabled}
        onAddFormat={addFormat}
        onRemoveFormat={removeFormat}
        onPatchFormat={patchFormat}
        creationProgress={creationProgress}
        permissionError={permissionError}
        submitError={submitError || formatDefaults.error}
        formatDefaults={formatDefaults}
        hasPartialProgress={hasPartialProgress}
      />
    );
  }, [
    activeStep,
    canReadTaxes,
    categories,
    categoriesError,
    categoriesLoading,
    creationProgress,
    disabled,
    errors,
    formatOptions,
    hasPartialProgress,
    permissionError,
    productDraft,
    productWasSaved,
    submitError,
    formatDefaults,
    taxes,
    taxesError,
    taxesLoading,
    varieties,
  ]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[1800] bg-slate-100">
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-wizard-title"
        aria-describedby="product-wizard-description"
        aria-busy={isSaving}
        className="flex h-[100dvh] min-h-0 flex-col"
        onKeyDown={handleDialogKeyDown}
      >
        <header
          className="shrink-0 border-b border-gray-200 bg-white px-4 pb-4 pt-4 shadow-sm sm:px-6 lg:px-8"
          style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top))' }}
        >
          <div className="mx-auto flex max-w-7xl items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">Catálogo</p>
              <h1 id="product-wizard-title" className="mt-1 text-xl font-semibold tracking-tight text-gray-950 sm:text-2xl">Crear producto completo</h1>
              <p id="product-wizard-description" className="mt-1 hidden text-sm text-gray-600 sm:block">
                Producto, variedades y detalles de formato en un solo flujo guiado.
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              disabled={isSaving}
              aria-label="Cerrar creación de producto"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-100 hover:text-gray-950 disabled:opacity-50"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
        </header>

        <Stepper activeStep={activeStep} highestStep={highestStep} onSelect={moveToStep} disabled={disabled} />
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          Paso {activeStep + 1} de {STEPS.length}: {STEPS[activeStep].label}. {STEPS[activeStep].description}.
        </p>
        <p className="sr-only" aria-live="polite" aria-atomic="true">{draftAnnouncement}</p>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
            {hasPartialProgress && activeStep !== STEPS.length - 1 ? (
              <p role="status" className="mb-5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                Parte de la estructura ya está guardada. Los elementos completados están bloqueados; puedes corregir los pendientes y reintentar.
              </p>
            ) : null}
            {stepContent}
          </main>
        </div>

        <footer
          className="shrink-0 border-t border-gray-200 bg-white/95 px-4 pb-4 pt-3 shadow-[0_-8px_24px_rgba(15,23,42,0.06)] backdrop-blur sm:px-6 lg:px-8"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto flex max-w-7xl flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-h-5 text-center text-xs text-gray-500 sm:text-left">
              {isSaving ? (
                <span role="status" className="font-medium text-blue-700">{saveStatus || 'Creando producto...'}</span>
              ) : activeStep === STEPS.length - 1 ? (
                <span>Revisa los datos. El guardado creará toda la estructura.</span>
              ) : (
                <span>Puedes volver a pasos anteriores antes de guardar.</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button
                type="button"
                onClick={activeStep === 0 ? requestClose : () => moveToStep(activeStep - 1)}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {activeStep === 0 ? 'Cancelar' : <><FiArrowLeft aria-hidden="true" /> Anterior</>}
              </button>
              {activeStep < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={disabled || (activeStep === 0 && categoriesLoading)}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Siguiente <FiArrowRight aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving || !canCompleteFlow}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving ? 'Creando...' : hasPartialProgress ? 'Reintentar' : <><FiCheck aria-hidden="true" /> Crear producto</>}
                </button>
              )}
            </div>
          </div>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default ProductCreationWizard;
